import type { Algorithm, LogLine, RunResult, StageStatus } from '../types'

// A stand-in for the real SSE stream from FastAPI. It walks the pipeline
// stage-by-stage with realistic delays, emitting logs and finally a result.
// Swapping this for an EventSource later keeps the same handler shape.

export interface RunHandlers {
  onStageStatus: (stageId: string, status: StageStatus) => void
  onLog: (line: LogLine) => void
  onResult: (result: RunResult) => void
  onDone: () => void
}

export interface RunHandle {
  cancel: () => void
}

const STAGE_DELAY: Record<string, number> = {
  dataset: 650,
  preprocess: 550,
  features: 750,
  model: 500,
  train: 1200,
  predict: 550,
}

function sigmoid(x: number): number {
  return 1 / (1 + Math.exp(-x))
}

function clamp(x: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, x))
}

function stageKind(stageId: string): string {
  return stageId.split('-').pop() ?? ''
}

// Turn the student's inputs into a plausible signal in roughly [-1, 1].
function computeSignal(algo: Algorithm, inputs: Record<string, number | string>): number {
  let acc = 0
  let weight = 0
  for (const f of algo.inputs) {
    const imp = algo.featureImportance.find((fi) => fi.name === f.name)?.value ?? 0.2
    const val = inputs[f.name]
    if (f.type === 'number') {
      const def = Number(f.default) || 1
      const ratio = (Number(val) - def) / (Math.abs(def) + 1)
      acc += imp * clamp(ratio, -1, 1)
      weight += imp
    } else if (f.type === 'select' && f.options) {
      const idx = f.options.indexOf(val)
      const n = f.options.length
      const norm = n > 1 ? idx / (n - 1) : 0.5
      acc += imp * (norm - 0.5) * 2
      weight += imp
    }
  }
  return weight ? acc / weight : 0
}

function money(v: number): string {
  return '$' + Math.round(v).toLocaleString('en-US')
}

function buildResult(algo: Algorithm, inputs: Record<string, number | string>): RunResult {
  const signal = computeSignal(algo, inputs)
  const metricName = algo.sampleMetric.name
  const metricValue = algo.sampleMetric.value
  const base = {
    kind: algo.output.kind,
    metricName,
    metricValue,
    featureImportance: algo.featureImportance,
  }

  if (algo.output.kind === 'image_class') {
    const label = String(inputs['sample_image'] ?? 'Healthy')
    const prob = 0.9 + clamp(signal, -0.05, 0.08)
    return {
      ...base,
      headline: label,
      detail: `${Math.round(prob * 100)}% confident`,
      probability: prob,
      label,
    }
  }

  if (algo.output.kind === 'regression') {
    const value = Math.max(0, (algo.output.base ?? 0) + signal * (algo.output.scale ?? 1))
    const unit = algo.output.unit ?? ''
    let headline: string
    if (unit === '$') headline = money(value)
    else if (unit === '★') headline = `${value.toFixed(1)} ★`
    else headline = `${Math.round(value).toLocaleString('en-US')} ${unit}`.trim()
    return {
      ...base,
      headline,
      detail: `point estimate · ${metricName} ${metricValue}`,
      value,
      unit,
    }
  }

  // classification
  const prob = clamp(sigmoid(2.2 * signal + 0.4), 0.02, 0.98)
  const isPositive = prob > 0.5
  const label = isPositive ? algo.output.positiveLabel ?? 'Positive' : algo.output.negativeLabel ?? 'Negative'
  return {
    ...base,
    headline: label,
    detail: `${Math.round(prob * 100)}% probability`,
    probability: prob,
    label,
  }
}

function logsForStage(algo: Algorithm, stageId: string): LogLine[] {
  const kind = stageKind(stageId)
  const { rows, features } = algo.dataset
  switch (kind) {
    case 'dataset':
      return [
        { stageId, kind: 'info', text: `loading ${algo.dataset.name}…` },
        { stageId, kind: 'metric', text: `${rows.toLocaleString('en-US')} rows × ${features} columns` },
      ]
    case 'preprocess':
      return [
        { stageId, kind: 'info', text: 'imputing nulls · encoding categoricals' },
        { stageId, kind: 'metric', text: `train/test split 80/20 → ${Math.round(rows * 0.8).toLocaleString('en-US')} train rows` },
      ]
    case 'features':
      return [{ stageId, kind: 'metric', text: `engineered ${features + 3} features` }]
    case 'model':
      return [{ stageId, kind: 'info', text: `initialising ${algo.name}` }]
    case 'train':
      return [
        { stageId, kind: 'info', text: 'fitting model on training set…' },
        { stageId, kind: 'metric', text: `${algo.sampleMetric.name} = ${algo.sampleMetric.value} on held-out test` },
      ]
    case 'predict':
      return [{ stageId, kind: 'info', text: 'running inference on your input…' }]
    default:
      return []
  }
}

export function runPipeline(
  algo: Algorithm,
  inputs: Record<string, number | string>,
  handlers: RunHandlers,
): RunHandle {
  let cancelled = false
  const timers: number[] = []

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      const t = window.setTimeout(resolve, ms)
      timers.push(t)
    })

  ;(async () => {
    for (const stage of algo.pipeline) {
      if (cancelled) return
      handlers.onStageStatus(stage.id, 'running')
      const lines = logsForStage(algo, stage.id)
      const delay = STAGE_DELAY[stageKind(stage.id)] ?? 600
      // dribble the log lines out across the stage's duration
      for (let i = 0; i < lines.length; i++) {
        await sleep(delay / (lines.length + 1))
        if (cancelled) return
        handlers.onLog(lines[i])
      }
      await sleep(delay / (lines.length + 1))
      if (cancelled) return
      handlers.onStageStatus(stage.id, 'done')
    }
    if (cancelled) return
    const result = buildResult(algo, inputs)
    handlers.onLog({ stageId: algo.pipeline[algo.pipeline.length - 1].id, kind: 'result', text: `▶ ${result.headline} — ${result.detail}` })
    handlers.onResult(result)
    handlers.onDone()
  })()

  return {
    cancel: () => {
      cancelled = true
      timers.forEach((t) => window.clearTimeout(t))
    },
  }
}
